import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast, ConfirmationModal } from '../components/ui/Notification';
import { 
  FiArrowLeft, FiSave, FiCopy, FiCheck, FiSend, FiFileText, 
  FiGlobe, FiCalendar, FiMail, FiMessageCircle, FiPhone, 
  FiLock, FiLoader, FiAlertCircle, FiInfo, FiTrash2, FiExternalLink,
  FiActivity, FiCheckCircle, FiClock, FiShield
} from 'react-icons/fi';

export default function AdminClientDetail() {
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const { adminUser } = useAuth();
  
  const [loading, setLoading] = useState(!!clientKey);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Staged Actions Logic
  const [testStage, setTestStage] = useState(0); 
  const [saveStage, setSaveStage] = useState(0);
  const [sheetStage, setSheetStage] = useState(0);
  const [testResult, setTestResult] = useState(null);
  
  // Modal State
  const [modal, setModal] = useState({ open: false, type: null });

  // Date Fix: Ensure yyyy-MM-dd format for HTML inputs
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const [formData, setFormData] = useState({
    client_key: '',
    client_name: '',
    business_name: '',
    website_domain: '',
    allowed_domains: '',
    status: 'Trial',
    plan: 'Standard',
    notes: '',
    service_start_date: formatDateForInput(new Date().toISOString()),
    service_end_date: '',
    grace_period_days: 7,
    disable_notifications_after_end_date: true,
    send_business_notification: true,
    business_notification_email: '',
    cc_email: '',
    reply_to_email: '',
    send_customer_confirmation: false,
    customer_ack_from_name: '',
    customer_confirmation_subject: 'Thank you for your enquiry',
    send_sms_alert: false,
    sms_mobile: '',
    client_portal_enabled: true,
    client_login_emails: '',
    client_login_code: '',
    client_portal_role: 'Client Lead',
    sheet_id: '',
    sheet_url: '',
    client_sheet_auto_write: true
  });

  const SCRIPT_BASE = import.meta.env.VITE_BPAS_PUBLIC_SCRIPT_BASE_URL;

  const scriptInfo = useMemo(() => {
    const key = formData.client_key || clientKey;
    const isSaved = !!key;
    return {
      code: isSaved && SCRIPT_BASE ? `<script async src="${SCRIPT_BASE}?k=${key}"></script>` : null,
      isSaved
    };
  }, [formData.client_key, clientKey, SCRIPT_BASE]);

  useEffect(() => {
    if (clientKey) {
      const fetchClient = async () => {
        try {
          const res = await adminApi.getClient(adminUser.admin_session_token, clientKey);
          if (res.success && res.client) {
            setFormData(prev => ({ 
              ...prev, 
              ...res.client,
              service_start_date: formatDateForInput(res.client.service_start_date),
              service_end_date: formatDateForInput(res.client.service_end_date)
            }));
          }
        } catch (e) {
          setNotification({ message: 'Failed to load client data.', type: 'error' });
        } finally {
          setLoading(false);
        }
      };
      fetchClient();
    }
  }, [clientKey, adminUser]);

  const validateForm = () => {
    const errors = { profile: [], notifications: [], portal: [] };
    if (!formData.client_name) errors.profile.push("Internal Client Name");
    if (!formData.business_name) errors.profile.push("Client Business Name");
    if (!formData.website_domain) errors.profile.push("Website Domain");
    if (formData.send_business_notification && !formData.business_notification_email) errors.notifications.push("Recipient Email (Business Alerts)");
    if (formData.send_customer_confirmation && !formData.reply_to_email) errors.notifications.push("Reply-to Address (Customer Ack)");
    if (formData.client_portal_enabled) {
      if (!formData.client_login_emails) errors.portal.push("Client Login Email(s)");
      if (!formData.client_login_code || formData.client_login_code.length !== 6) errors.portal.push("Client Portal Passcode (6 digits)");
    }
    const hasErrors = errors.profile.length > 0 || errors.notifications.length > 0 || errors.portal.length > 0;
    setValidationErrors(hasErrors ? errors : {});
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaveStage(1); 
    
    if (!validateForm()) {
      setSaveStage(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setSaveStage(2);
    
    try {
      setTimeout(() => setSaveStage(3), 500);
      const res = clientKey 
        ? await adminApi.updateClient(adminUser.admin_session_token, { ...formData, client_key: clientKey })
        : await adminApi.addClient(adminUser.admin_session_token, formData);

      setSaveStage(4);
      
      if (res.success) {
        setSaveStage(5);
        const updatedKey = res.client?.client_key || res.client_key;
        if (updatedKey) setFormData(prev => ({ ...prev, client_key: updatedKey }));
        
        setSaveStage(6);
        setNotification({ message: 'Client configuration saved successfully.', type: 'success' });
        if (updatedKey) setNotification({ message: 'Install script is ready.', type: 'info' });

        if (!clientKey && updatedKey) {
          setTimeout(() => navigate(`/admin/clients/${updatedKey}`, { replace: true }), 1000);
        }
      } else {
        setSaveStage(0);
        setNotification({ message: res.message || 'Could not save client configuration.', type: 'error' });
      }
    } catch (e) {
      setSaveStage(0);
      setNotification({ message: 'Gateway connection failed.', type: 'error' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStage(0), 3000);
    }
  };

  const handleSendTestLead = async () => {
    if (!clientKey) {
      setNotification({ message: 'Save the client first before sending a test lead.', type: 'error' });
      return;
    }

    setTestResult(null);
    setTestStage(1);
    
    try {
      setTimeout(() => setTestStage(2), 1000);
      const res = await adminApi.sendTestLead(adminUser.admin_session_token, clientKey);
      setTestStage(3);
      
      if (res.success) {
        setTestStage(4);
        setTimeout(() => setTestStage(5), 1000);
        setTestResult(res);
        
        if (res.notification_status?.toLowerCase().includes('failed')) {
          setNotification({ 
            message: 'Lead created, but one or more notifications failed. Check Logs.', 
            type: 'warning' 
          });
        } else {
          setNotification({ message: 'Test lead created successfully.', type: 'success' });
        }
      } else {
        setNotification({ message: res.message || 'Test lead failed.', type: 'error' });
      }
    } catch (e) {
      setNotification({ message: 'Test lead transmission failed.', type: 'error' });
    } finally {
      setTestStage(6);
    }
  };

  const handleCreateSheet = async () => {
    setSheetStage(1);
    setNotification({ message: 'Creating client lead sheet...', type: 'info' });
    
    try {
      setTimeout(() => setSheetStage(2), 1000);
      const res = await adminApi.createSheet(
        adminUser.admin_session_token, 
        clientKey, 
        formData.business_notification_email
      );
      
      setSheetStage(3);
      if (res.success) {
        setSheetStage(4);
        setFormData(prev => ({ ...prev, sheet_id: res.sheet_id, sheet_url: res.sheet_url }));
        setSheetStage(5);
        setNotification({ message: 'Client lead sheet created successfully.', type: 'success' });
      } else {
        setSheetStage(0);
        setNotification({ message: res.message || 'Could not create sheet.', type: 'error' });
      }
    } catch (e) {
      setSheetStage(0);
      setNotification({ message: 'Sheet creation failed.', type: 'error' });
    } finally {
      setTimeout(() => setSheetStage(0), 3000);
    }
  };

  if (loading) return <div className="flex items-center justify-center p-20"><SafeIcon icon={FiLoader} className="animate-spin text-4xl text-brand-blue" /></div>;

  return (
    <div className="space-y-8 pb-32">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <ConfirmationModal 
        isOpen={modal.open && modal.type === 'createSheet'}
        title="Create client lead sheet?"
        message="This will create a separate Google Sheet for this client's leads. Your master BPAS sheet will remain private."
        confirmText="Create Sheet"
        loading={sheetStage > 0 && sheetStage < 5}
        onConfirm={() => {
          setModal({ open: false, type: null });
          handleCreateSheet();
        }}
        onCancel={() => setModal({ open: false, type: null })}
      />

      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate('/admin/clients')} className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-slate-400 hover:text-navy-900 transition-colors border border-slate-100">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tighter">
              {clientKey ? (formData.business_name || 'Edit Client') : 'New Client'}
            </h1>
            <p className="text-slate-400 text-[10px] uppercase font-black tracking-widest mt-1 opacity-60">
              {clientKey ? `ID: ${clientKey}` : 'Provisioning Wizard'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex flex-col items-end mr-4">
            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Configuration Tools</span>
            <div className="flex gap-2">
              <button 
                type="button" 
                onClick={handleSendTestLead}
                disabled={testStage > 0 && testStage < 6}
                title="Send Sample Submission"
                className="w-10 h-10 bg-brand-cyan/10 text-brand-cyan rounded-xl flex items-center justify-center hover:bg-brand-cyan hover:text-white transition-all disabled:opacity-50"
              >
                <SafeIcon icon={testStage > 0 && testStage < 6 ? FiLoader : FiSend} className={testStage > 0 && testStage < 6 ? 'animate-spin' : ''} />
              </button>
              <button 
                type="button" 
                onClick={() => setModal({ open: true, type: 'createSheet' })}
                disabled={!!formData.sheet_id || (sheetStage > 0 && sheetStage < 5)}
                title="Create Google Sheet"
                className="w-10 h-10 bg-emerald-500/10 text-emerald-500 rounded-xl flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-50"
              >
                <SafeIcon icon={(sheetStage > 0 && sheetStage < 5) ? FiLoader : FiFileText} className={(sheetStage > 0 && sheetStage < 5) ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          <button 
            type="submit" 
            form="clientForm"
            disabled={saving} 
            className="px-8 py-4 bg-brand-blue text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-brand-blue/30 flex items-center gap-3 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiSave} /> {clientKey ? 'Update Configuration' : 'Save & Provision'}</>}
          </button>
        </div>
      </header>

      {/* Summary Pulse Strip */}
      {clientKey && (
        <div className="bg-white rounded-3xl p-4 border border-slate-100 shadow-sm flex flex-wrap items-center justify-between gap-6 px-8">
          <div className="flex items-center gap-8">
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Client Status</span>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${formData.status === 'Active' ? 'bg-emerald-500' : 'bg-brand-blue'}`} />
                <span className="text-xs font-black text-slate-900 uppercase">{formData.status}</span>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-100 hidden sm:block" />
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Portal Access</span>
              <span className={`text-xs font-black uppercase ${formData.client_portal_enabled ? 'text-emerald-500' : 'text-slate-400'}`}>
                {formData.client_portal_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="w-px h-8 bg-slate-100 hidden sm:block" />
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Business Alerts</span>
              <span className={`text-xs font-black uppercase ${formData.send_business_notification ? 'text-emerald-500' : 'text-slate-400'}`}>
                {formData.send_business_notification ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
             <span className="text-[10px] font-bold text-slate-400 italic">ID: {clientKey}</span>
             {formData.sheet_url && (
               <a href={formData.sheet_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[10px] font-black uppercase text-emerald-600 hover:underline">
                 <SafeIcon icon={FiExternalLink} /> Open Lead Sheet
               </a>
             )}
          </div>
        </div>
      )}

      {/* Pipeline Progress */}
      {(saveStage > 0 || testStage > 0 || sheetStage > 0) && (
        <div className="bg-navy-900 text-white rounded-[2rem] p-6 border border-white/5 shadow-2xl flex items-center justify-between animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-brand-cyan/20 rounded-xl flex items-center justify-center text-brand-cyan">
              <SafeIcon icon={FiLoader} className="animate-spin" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">System Pipeline</p>
              <p className="text-sm font-bold">
                {saveStage > 0 && [
                  '', 'Validating fields...', 'Preparing configuration...', 'Sending to gateway...', 'Saving database...', 'Refreshing state...', 'Configuration saved!'
                ][saveStage]}
                {testStage > 0 && [
                  '', 'Preparing lead...', 'Sending to gateway...', 'Waiting for GAS...', 'Creating lead...', 'Sending alerts...', 'Finalising result...'
                ][testStage]}
                {sheetStage > 0 && [
                  '', 'Preparing request...', 'Creating Sheet...', 'Linking client...', 'Saving metadata...', 'Sheet created!'
                ][sheetStage]}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className={`w-8 h-1 rounded-full transition-all duration-500 ${
                (saveStage >= i || testStage >= i || (sheetStage >= i && i <= 5)) ? 'bg-brand-cyan' : 'bg-white/10'
              }`} />
            ))}
          </div>
        </div>
      )}

      {/* Test Lead Diagnostic Card */}
      {testResult && (
        <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center justify-between mb-6">
             <h4 className="font-black uppercase tracking-widest text-xs text-slate-900">Test Execution Diagnostic</h4>
             <span className="px-2 py-1 rounded text-[9px] font-black uppercase bg-emerald-100 text-emerald-600">Transmission Success</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
            {[
              { label: 'Lead Created', val: testResult.success ? 'Yes' : 'No', icon: FiCheckCircle },
              { label: 'Lead ID', val: testResult.lead_id?.slice(0,8) || 'N/A', icon: FiActivity },
              { label: 'Business Email', val: testResult.notification_status?.toLowerCase().includes('failed') ? 'Failed' : 'Success', icon: FiMail },
              { label: 'Service Health', val: testResult.service_status || 'Stable', icon: FiShield }
            ].map(r => (
              <div key={r.label} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                   <SafeIcon icon={r.icon} className="text-xs text-slate-400" />
                   <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{r.label}</p>
                </div>
                <p className={`text-xs font-black truncate ${r.val === 'Failed' ? 'text-red-500' : 'text-slate-900'}`}>{r.val}</p>
              </div>
            ))}
          </div>
          <div className="bg-brand-blue/5 p-4 rounded-2xl border border-brand-blue/10">
            <p className="text-[10px] font-bold text-slate-600 flex items-center gap-2">
              <SafeIcon icon={FiInfo} className="text-brand-blue" />
              Note: If lead creation succeeded but email failed, check the Google Sheet Logs tab and EmailIt Script Properties.
            </p>
          </div>
        </div>
      )}

      <form id="clientForm" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          {/* Business Profile */}
          <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-full -mr-16 -mt-16" />
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-brand-blue/5 rounded-xl flex items-center justify-center text-brand-blue"><SafeIcon icon={FiGlobe} /></div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Business Profile</h3>
              </div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest ml-13">Core business identity and domain details.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Internal Client Name <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} placeholder="e.g. John Smith" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Client Business Name <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="e.g. Smith & Co Legal" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Website Domain <span className="text-red-400">*</span></label>
                <input required type="text" value={formData.website_domain} onChange={e => setFormData({...formData, website_domain: e.target.value})} placeholder="e.g. smithlegal.com.au" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white" />
                <p className="text-[9px] text-slate-400 font-bold italic ml-1">Used to associate captured leads with this client.</p>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Plan Level</label>
                <select value={formData.plan} onChange={e => setFormData({...formData, plan: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all focus:bg-white appearance-none cursor-pointer">
                  <option>Standard</option><option>Premium</option><option>Enterprise</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification Engine */}
          <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-500/5 rounded-xl flex items-center justify-center text-emerald-500"><SafeIcon icon={FiMail} /></div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Notification Engine</h3>
              </div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest ml-13">Controls business alerts, acknowledgements, and communication behaviour.</p>
            </div>

            <div className="space-y-10">
              <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest text-navy-900">Business Alerts</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Send lead notifications to the client</p>
                </div>
                <input type="checkbox" checked={formData.send_business_notification} onChange={e => setFormData({...formData, send_business_notification: e.target.checked})} className="w-6 h-6 rounded-lg text-brand-blue border-slate-300 focus:ring-brand-blue" />
              </div>
              
              {formData.send_business_notification && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 animate-in fade-in slide-in-from-top-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Recipient Email <span className="text-red-400">*</span></label>
                    <input type="email" value={formData.business_notification_email} onChange={e => setFormData({...formData, business_notification_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                    <p className="text-[9px] text-slate-400 font-bold italic ml-1">Business notification emails are sent here.</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">CC Email (Optional)</label>
                    <input type="email" value={formData.cc_email} onChange={e => setFormData({...formData, cc_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                  </div>
                </div>
              )}
              
              <hr className="border-slate-50" />
              
              <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest text-navy-900">Customer Acknowledgement</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Send auto-reply message to website visitor</p>
                </div>
                <input type="checkbox" checked={formData.send_customer_confirmation} onChange={e => setFormData({...formData, send_customer_confirmation: e.target.checked})} className="w-6 h-6 rounded-lg text-brand-blue border-slate-300 focus:ring-brand-blue" />
              </div>

              {formData.send_customer_confirmation && (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Reply-to Address <span className="text-red-400">*</span></label>
                      <input type="email" value={formData.reply_to_email} onChange={e => setFormData({...formData, reply_to_email: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                      <p className="text-[9px] text-slate-400 font-bold italic ml-1">Customer replies will go to this address.</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Sender Name (from)</label>
                      <input type="text" value={formData.customer_ack_from_name} onChange={e => setFormData({...formData, customer_ack_from_name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent focus:border-brand-blue/10 rounded-2xl text-sm font-bold outline-none transition-all" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-8">
          {/* Operational Checklist */}
          <div className="bg-navy-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-full blur-3xl -mr-16 -mt-16" />
            <h3 className="text-lg font-black mb-6 uppercase tracking-tight relative z-10 flex items-center gap-2">
               <SafeIcon icon={FiActivity} className="text-brand-cyan" /> Next Steps
            </h3>
            <div className="space-y-4 relative z-10">
              {[
                { label: 'Complete core profile', done: !!formData.business_name && !!formData.website_domain },
                { label: 'Save configuration', done: !!clientKey },
                { label: 'Install script on website', done: !!clientKey },
                { label: 'Run successful test lead', done: !!testResult },
                { label: 'Share portal with client', done: formData.client_portal_enabled && !!formData.client_login_emails }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${step.done ? 'bg-emerald-500 text-white' : 'border border-white/20'}`}>
                    {step.done && <SafeIcon icon={FiCheck} className="text-[10px]" />}
                  </div>
                  <span className={`text-xs font-bold ${step.done ? 'text-white' : 'text-slate-400'}`}>{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-amber-500/5 rounded-xl flex items-center justify-center text-amber-500"><SafeIcon icon={FiCalendar} /></div>
                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Lifecycle</h3>
              </div>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Service and billing dates.</p>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Service Start Date</label>
                <input type="date" value={formData.service_start_date} onChange={e => setFormData({...formData, service_start_date: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Service End Date</label>
                <input type="date" value={formData.service_end_date} onChange={e => setFormData({...formData, service_end_date: e.target.value})} className="w-full px-5 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none" />
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Account Status</span>
                <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="bg-slate-100 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none appearance-none cursor-pointer">
                  <option>Trial</option><option>Active</option><option>Grace</option><option>Expired</option><option>Paused</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-brand-cyan/5 rounded-xl flex items-center justify-center text-brand-cyan"><SafeIcon icon={FiLock} /></div>
                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Portal Access</h3>
              </div>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">How the client logs into their portal.</p>
            </div>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-900">Enable Portal</p>
                  <p className="text-[9px] text-slate-400 font-medium">Allow read-only portal login</p>
                </div>
                <input type="checkbox" checked={formData.client_portal_enabled} onChange={e => setFormData({...formData, client_portal_enabled: e.target.checked})} className="w-5 h-5 rounded text-brand-blue border-slate-300" />
              </div>
              {formData.client_portal_enabled && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Client Login Email(s) <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.client_login_emails} onChange={e => setFormData({...formData, client_login_emails: e.target.value})} placeholder="comma-separated emails" className="w-full px-4 py-3 bg-slate-50 rounded-xl text-[11px] font-bold outline-none" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Client Portal Passcode (6 digits) <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.client_login_code} onChange={e => setFormData({...formData, client_login_code: e.target.value.replace(/\D/g, '').slice(0, 6)})} placeholder="••••••" className="w-full px-4 py-3 bg-slate-50 rounded-xl text-lg font-mono font-black tracking-[0.5em] outline-none" />
                    <p className="text-[9px] text-slate-400 font-bold leading-tight italic ml-1">
                      Note: This is for the client portal only, not BPAS admin login.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Integration Script */}
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm relative overflow-hidden">
            <h3 className="text-lg font-black mb-2 uppercase tracking-tight">Integration Script</h3>
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-6">Install on client website to capture leads.</p>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 relative z-10 min-h-[100px] flex items-center justify-center">
              {scriptInfo.code ? (
                <code className="text-[10px] text-brand-blue break-all font-mono leading-relaxed select-all">
                  {scriptInfo.code}
                </code>
              ) : (
                <div className="flex gap-3 text-red-300 text-[10px] font-medium leading-relaxed italic text-center">
                  <p>{!scriptInfo.isSaved ? 'Save client first to generate script.' : 'Public lead.js URL is missing.'}</p>
                </div>
              )}
            </div>
            <button 
              type="button" 
              onClick={async () => {
                if (!scriptInfo.code) {
                  setNotification({ message: 'Install script is not ready yet. Save the client first.', type: 'warning' });
                  return;
                }
                await navigator.clipboard.writeText(scriptInfo.code);
                setCopied(true);
                setNotification({ message: 'Install script copied.', type: 'success' });
                setTimeout(() => setCopied(false), 2000);
              }}
              className="w-full mt-6 py-4 bg-navy-900 text-white hover:bg-navy-800 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all group shadow-lg"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              {copied ? 'Copied' : 'Copy Install Script'}
            </button>
            <p className="text-[9px] text-slate-400 font-bold italic mt-4 text-center">Paste this once on the client website, ideally before &lt;/body&gt;.</p>
          </div>
        </div>
      </form>

      {/* Sticky Action Footer (Desktop Only) */}
      <div className="hidden lg:flex fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-slate-100 p-6 z-[40] pl-64 items-center justify-center">
         <div className="max-w-6xl w-full flex items-center justify-between px-10">
            <div className="flex items-center gap-4">
               <SafeIcon icon={FiActivity} className="text-brand-blue" />
               <p className="text-xs font-bold text-slate-500">You are editing <span className="text-slate-900 font-black">{formData.business_name || 'New Client'}</span></p>
            </div>
            <button 
              type="submit" 
              form="clientForm"
              disabled={saving} 
              className="px-12 py-4 bg-brand-blue text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-brand-blue/30 flex items-center gap-3 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            >
              {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiSave} /> {clientKey ? 'Update Configuration' : 'Save & Provision'}</>}
            </button>
         </div>
      </div>
    </div>
  );
}